<template>
  <!-- Link a contract to a system, or change an existing link: what it covers and the processing role (US 9370) -->
  <V2SidePanel ref="panel"
               :kind="$t('system_link.kind')"
               :title="link ? $t('system_link.edit_title') : $t('system_link.title')"
               :intro="$t('system_link.intro', { name: system.name })"
               :busy="saving"
               qid="system-link-contract-panel"
               @hidden="reset">
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <div class="v2-field">
      <span class="v2-field__label">{{ $t('system_link.contract') }} <span class="v2-field__req">*</span></span>
      <div v-if="link" class="sx-option on">
        <font-awesome-icon icon="file-signature"/>
        <div class="sx-option__grow">{{ link.name }}</div>
      </div>
      <template v-else>
        <b-form-input v-model="keyword"
                      type="search"
                      :placeholder="$t('system_link.search')"
                      qid="system-link-search"
                      @input="search"></b-form-input>
        <div class="sx-options" role="listbox">
          <button v-for="contract in contracts"
                  :key="contract.id"
                  type="button"
                  role="option"
                  class="sx-option"
                  :class="{ on: selected && selected.id === contract.id }"
                  :aria-selected="selected && selected.id === contract.id ? 'true' : 'false'"
                  :disabled="linkedIds.includes(contract.id)"
                  :qid="'system-link-option-' + contract.id"
                  @click="pick(contract)">
            <span class="sx-option__logo">{{ contract.name.slice(0, 1).toUpperCase() }}</span>
            <span class="sx-option__grow">
              {{ contract.name }}
              <small>{{ contractMeta(contract) }}</small>
            </span>
            <font-awesome-icon v-if="selected && selected.id === contract.id" icon="check"/>
          </button>
          <p v-if="loaded && !contracts.length" class="sx-options__empty">{{ $t('system_link.none') }}</p>
          <router-link v-if="canCreateContract"
                       :to="{ name: 'contracts.create' }"
                       target="_blank"
                       class="sx-option is-new"
                       qid="system-link-create">
            <font-awesome-icon icon="plus"/>
            <span class="sx-option__grow">
              {{ keyword ? $t('system_link.create_named', { name: keyword }) : $t('system_link.create') }}
              <small>{{ $t('system_link.create_hint') }}</small>
            </span>
          </router-link>
        </div>
      </template>
    </div>

    <div class="v2-field">
      <span class="v2-field__label">{{ $t('system_link.covers') }}</span>
      <div class="v2-field__two">
        <div v-for="group in coversGroups" :key="group.key">
          <div class="sx-options__head">{{ $t('system_page.where.groups.' + group.key).split(' · ')[0] }}</div>
          <div class="sx-options">
            <button v-for="value in group.values"
                    :key="value"
                    type="button"
                    class="sx-option"
                    :class="{ on: covers === value }"
                    :aria-pressed="covers === value ? 'true' : 'false'"
                    :qid="'system-link-covers-' + value"
                    @click="covers = covers === value ? null : value">
              <font-awesome-icon v-if="covers === value" icon="check"/>{{ $t('systems_overview.covers.' + value) }}
            </button>
          </div>
        </div>
      </div>
      <small v-if="suggestion" class="v2-field__note sx-suggest">
        <font-awesome-icon icon="sparkles"/>
        {{ $t('system_link.suggested_' + suggestion.reason, { word: suggestion.word, covers: $t('systems_overview.covers.' + suggestion.covers) }) }}
      </small>
      <small v-else class="v2-field__note">{{ $t('system_link.covers_hint') }}</small>
    </div>

    <div class="v2-field">
      <span class="v2-field__label">{{ $t('systems.third_parties.create.processing_role_label') }} <span class="v2-field__req">*</span></span>
      <div class="sx-toggles">
        <button v-for="role in roles"
                :key="role.value"
                type="button"
                :class="{ on: processingRole === role.value }"
                :aria-pressed="processingRole === role.value ? 'true' : 'false'"
                :qid="'system-link-role-' + role.value"
                @click="processingRole = role.value">
          <font-awesome-icon v-if="processingRole === role.value" icon="check"/>{{ role.text }}
        </button>
      </div>
    </div>

    <template #foot>
      <span class="v2-panel__grow"></span>
      <b-button variant="secondary" @click="$refs.panel.hide()">{{ $t('systems.create.cancel') }}</b-button>
      <b-button variant="success" :disabled="saving || !(link || selected) || !processingRole" qid="system-link-submit" @click="save">
        <font-awesome-icon :icon="saving ? 'spinner' : 'link'" :class="{ 'fa-spin': saving }"/>
        {{ link ? $t('system_hero.panel.save') : $t('system_link.submit') }}
      </b-button>
    </template>
  </V2SidePanel>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import V2SidePanel from '@/components/V2SidePanel.vue';

  export default {
    name: 'SystemLinkContractPanel',
    components: {
      V2SidePanel
    },
    props: {
      system: { type: Object, required: true },
      // Contracts already linked, from the system summary
      linked: { type: Array, default: () => [] }
    },
    data: function() {
      return {
        link: null,
        keyword: '',
        contracts: [],
        loaded: false,
        selected: null,
        covers: null,
        coversTouched: false,
        processingRole: null,
        suggestion: null,
        saving: false,
        error: '',
        searchTimer: null,
        coversGroups: [
          { key: 'runs_on', values: ['software_hosting', 'hosting', 'licence'] },
          { key: 'access', values: ['support', 'works_in'] }
        ],
        roles: [
          { value: 'Data processor', text: this.$t('maintenance.third_parties.processing_role.data_processor') },
          { value: 'Data controller', text: this.$t('maintenance.third_parties.processing_role.data_controller') },
          { value: 'Joint controller', text: this.$t('activities.processing_role.joint_controller_label') }
        ]
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      linkedIds() {
        return this.linked.map(link => link.id);
      },
      canCreateContract() {
        return this.isGranted(this.permissions.THIRD_PARTY_CREATE);
      }
    },
    watch: {
      covers: function(value, old) {
        if (old !== undefined && this.suggestion && value !== this.suggestion.covers) {
          this.coversTouched = true;
        }
      }
    },
    methods: {
      // With a link, the panel changes that link; without one, it links a new contract
      show(link) {
        this.link = link || null;
        if (link) {
          this.covers = link.covers || null;
          this.processingRole = link.processing_role || null;
        } else {
          this.loadContracts();
          window.addEventListener('focus', this.loadContracts);
        }
        this.$refs.panel.show();
      },
      reset() {
        window.removeEventListener('focus', this.loadContracts);
        Object.assign(this, { link: null, keyword: '', selected: null, covers: null, coversTouched: false,
          processingRole: null, suggestion: null, error: '' });
      },
      contractMeta(contract) {
        let parts = [];
        if (contract.vendors && contract.vendors.length) {
          parts.push(contract.vendors.map(vendor => vendor.name).join(', '));
        }
        if (this.linkedIds.includes(contract.id)) {
          parts.push(this.$t('system_link.already'));
        }
        return parts.join(' · ');
      },
      search() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(this.loadContracts, 300);
      },
      loadContracts() {
        let self = this;
        // The system's own workspace decides which contracts it may use (a parent can open a partition's system)
        piincHttp.get('third-parties', { params: {
          client: this.system.client || this.getClient.slug,
          per_page: 30,
          relations: 1,
          keyword: this.keyword || undefined
        } }).then(function(response) {
          self.contracts = response.data.items;
          self.loaded = true;
        }, function() {});
      },
      pick(contract) {
        this.selected = contract;
        this.suggest();
      },
      // The suggestion fills in Covers until the user picks something else
      suggest() {
        let self = this;
        if (!this.selected) {
          return;
        }
        piincHttp.get('systems/' + this.system.slug + '/covers-suggestion', { params: {
          client: this.getClient.slug,
          third_party_id: this.selected.id,
          processing_role: this.processingRole || undefined
        } }).then(function(response) {
          self.suggestion = response.data.suggestion;
          if (self.suggestion && !self.coversTouched) {
            self.covers = self.suggestion.covers;
          }
        }, function() {});
      },
      save() {
        let self = this;
        let request = this.link
          ? piincHttp.put('systems/' + this.system.slug + '/third-parties/' + this.link.link_id, {
              third_party_id: this.link.id, processing_role: this.processingRole, covers: this.covers })
          : piincHttp.post('systems/' + this.system.slug + '/third-parties', {
              third_party_id: this.selected.id, processing_role: this.processingRole, covers: this.covers });

        this.saving = true;
        this.error = '';
        request.then(function() {
          self.saving = false;
          self.$toastr('success', self.link ? self.$t('systems_overview.saved') : self.$t('systems.third_parties.create.success'));
          self.$refs.panel.hide();
          self.$emit('saved');
        }, function(error) {
          self.saving = false;
          self.error = (error.data && error.data.message) || self.$t('system_hero.panel.error');
        });
      }
    },
    beforeDestroy() {
      window.removeEventListener('focus', this.loadContracts);
    }
  }
</script>
